import React, { createContext, useContext, useEffect, useState } from "react";

const LanguageContext = createContext({
    lang: "pt",
    setLang: () => {},
    toggle: () => {},
});

export const LanguageProvider = ({ children }) => {
    const [lang, setLangState] = useState(() => {
        try {
            return localStorage.getItem("nx_lang") === "en" ? "en" : "pt";
        } catch {
            return "pt";
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem("nx_lang", lang);
        } catch {
            /* ignore */
        }
        document.documentElement.setAttribute("lang", lang === "en" ? "en" : "pt-AO");
    }, [lang]);

    // Persist + smooth copper fade-out before reload.
    // Several screens (Dashboard, TopBar, etc.) capture translation strings into
    // module-level constants at import time. A reload guarantees every surface
    // re-evaluates against the new language. A 280ms fade-out makes the
    // refresh feel intentional rather than glitchy when toggled mid-presentation.
    //
    // Also PATCHes the server-side user preference so locale-aware AI surfaces
    // (triage, draft-reply, NLQ, sales co-pilot, morning briefing) line up
    // with the visible UI chrome. Falls back silently when unauthenticated.
    const applyLang = (next) => {
        try {
            localStorage.setItem("nx_lang", next);
        } catch {
            /* ignore */
        }
        if (next === lang) return;

        // Fire-and-forget server-side sync. The reload happens regardless of
        // network outcome so the demo never blocks on the API.
        (async () => {
            try {
                const m = await import("@/lib/api");
                if (m.getAccessToken && m.getAccessToken()) {
                    await m.apiPatch("/auth/me", { preferred_language: next });
                }
            } catch {
                /* 401 on /login is expected — UI flip continues */
            }
        })();

        // Insert a copper-tinted overlay that fades in over the current view,
        // then reload once it's fully opaque.
        try {
            const overlay = document.createElement("div");
            overlay.setAttribute("data-testid", "lang-switch-overlay");
            overlay.style.cssText = [
                "position:fixed",
                "inset:0",
                "z-index:99999",
                "pointer-events:auto",
                "background:radial-gradient(circle at 50% 50%, rgba(200,121,65,0.92) 0%, rgba(160,97,46,0.96) 70%, rgba(95,58,28,0.99) 100%)",
                "opacity:0",
                "transition:opacity 260ms cubic-bezier(0.4,0,0.2,1)",
                "display:flex",
                "align-items:center",
                "justify-content:center",
                "font-family:Georgia, serif",
                "color:#FFF7ED",
                "font-size:18px",
                "letter-spacing:0.08em",
            ].join(";");
            overlay.textContent = next === "en" ? "Switching to English…" : "A mudar para Português…";
            document.body.appendChild(overlay);
            // Trigger transition on next frame
            requestAnimationFrame(() => {
                overlay.style.opacity = "1";
            });
            setTimeout(() => {
                window.location.reload();
            }, 320);
        } catch {
            window.location.reload();
        }
    };

    const setLang = (l) => applyLang(l === "en" ? "en" : "pt");
    const toggle = () => applyLang(lang === "en" ? "pt" : "en");

    return (
        <LanguageContext.Provider value={{ lang, setLang, toggle }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);
